/**
 * UI material authoring pipeline (UI-19) — the compute-kernel architecture mirrored for UI:
 * declaration-level SCAN (is this .slang a UI material?), APPEND-only WRAPPER (user lines stay
 * 1:1 for diagnostics), and the content-addressed cache addressing shared with the runtime.
 * Pure string logic — the editor importer owns the actual Slang compile.
 *
 * Authoring contract (see shaders/modules/engine/uimaterial.slang):
 *   import engine.uimaterial;
 *   struct MyMat : IUIMaterial { float4 shade(UIMaterialInput i) { ... } }
 * One material struct per file; no custom resources in v1 (fields/module bindings rejected).
 */
import { computeShaderId } from '@engine/render2d';

/** VFS directory holding content-addressed compiled UI material WGSL (`<id>.wgsl`). */
export const UI_SHADER_CACHE_DIR = '/.cache/ui-shaders';

export function uiShaderPath(id: string): string {
  return `${UI_SHADER_CACHE_DIR}/${id}.wgsl`;
}

/** Content-addressed id over the WRAPPED source (same cyrb53/format-tag scheme as compute/vfx). */
export function uiShaderId(wrappedSource: string): string {
  return computeShaderId(wrappedSource);
}

export interface UIMaterialScan {
  /** True when the file declares a `struct X : IUIMaterial` — the marker for the importer branch. */
  isUIMaterial: boolean;
  /** The material struct name (first declaration when the file is invalid). */
  name: string | null;
  errors: { message: string; line: number }[];
}

/** Blank comments/strings preserving newlines (kept in sync with compute's kernelScan). */
function blankCommentsAndStrings(source: string): string {
  const out = source.split('');
  const n = source.length;
  let i = 0;
  const blank = (from: number, to: number): void => {
    for (let k = from; k < to; k++) if (out[k] !== '\n') out[k] = ' ';
  };
  while (i < n) {
    const c = source[i];
    const next = source[i + 1];
    if (c === '/' && next === '/') {
      const end = source.indexOf('\n', i);
      const stop = end === -1 ? n : end;
      blank(i, stop);
      i = stop;
    } else if (c === '/' && next === '*') {
      const end = source.indexOf('*/', i + 2);
      const stop = end === -1 ? n : end + 2;
      blank(i, stop);
      i = stop;
    } else if (c === '"') {
      let j = i + 1;
      while (j < n && source[j] !== '"' && source[j] !== '\n') {
        if (source[j] === '\\') j++;
        j++;
      }
      const stop = Math.min(j + 1, n);
      blank(i, stop);
      i = stop;
    } else {
      i++;
    }
  }
  return out.join('');
}

function lineOf(source: string, index: number): number {
  let line = 1;
  for (let i = 0; i < index && i < source.length; i++) if (source[i] === '\n') line++;
  return line;
}

/** Detect the material declaration. Interface conformance IS the marker — no attribute. */
export function scanUIMaterial(source: string): UIMaterialScan {
  const blanked = blankCommentsAndStrings(source);
  const errors: UIMaterialScan['errors'] = [];

  const decls = [...blanked.matchAll(/\bstruct\s+([A-Za-z_]\w*)\s*:\s*([^{]+)\{/g)].filter((m) =>
    /\bIUIMaterial\b/.test(m[2]!),
  );
  if (decls.length === 0) return { isUIMaterial: false, name: null, errors };

  const name = decls[0]![1]!;
  if (decls.length > 1) {
    errors.push({
      message: `one UI material struct per file (found ${decls.map((m) => m[1]).join(', ')})`,
      line: lineOf(blanked, decls[1]!.index!),
    });
  }
  if (!/\bimport\s+engine\s*\.\s*uimaterial\s*;/.test(blanked)) {
    errors.push({ message: `UI material '${name}' requires 'import engine.uimaterial;'`, line: 1 });
  }
  const entryClash = /\b(vs_main|fs_main)\b/.exec(blanked);
  if (entryClash) {
    errors.push({
      message: `'${entryClash[1]}' is reserved — the engine appends the UI material entry points`,
      line: lineOf(blanked, entryClash.index),
    });
  }
  return { isUIMaterial: true, name, errors };
}

export interface PreparedUIMaterial {
  wrappedSource: string;
  /** Lines 1..userLineCount are the user's own (diagnostics beyond it come from the wrapper). */
  userLineCount: number;
}

/**
 * Append the engine entry-point wrapper (compute's `prepareComputeModule` idiom: user source is
 * untouched, Slang's out-of-order globals let the wrapper call user symbols above). The wrapper
 * mirrors ui-rect's vertex/coverage path and shares its ANTIALIAS @id(0) pipeline-variant seam.
 */
export function prepareUIMaterialModule(source: string, scan: UIMaterialScan): PreparedUIMaterial {
  const name = scan.name ?? 'Material';
  const wrapper = `
// ---- engine-generated UI material wrapper (appended at import — user lines above are 1:1) ----
struct RpUIMatVSInput {
    [vk::location(0)] float2 position : TEXCOORD0;
    [vk::location(1)] float2 local    : TEXCOORD1;
    [vk::location(2)] float4 color    : TEXCOORD2;
    [vk::location(3)] float4 params   : TEXCOORD3;
};
struct RpUIMatVSOutput {
    float4 position : SV_Position;
    [vk::location(0)] float2 local     : TEXCOORD0;
    [vk::location(1)] float4 color     : COLOR0;
    [vk::location(2)] float4 params    : TEXCOORD1;
    [vk::location(3)] float2 screenPos : TEXCOORD2; // physical px, drives the rounded overflow-clip
};
struct RpUIMatUniforms {
    float4x4 viewProjection;
    float4 aux;        // .x = UI clock seconds (byte-64 slot); clipRect/clipParams follow at 80/96
    float4 clipRect;   // rounded overflow-clip footprint (cx, cy, halfX, halfY) physical px
    float4 clipParams; // (radius px, enabled, 0, 0) — set per-batch by UIBatch for clipped subtrees
    float4 hom0;       // UI-17c 3D inverse-homography cols (a,b,g)+flag / (c,d,h) / (e,f,1)
    float4 hom1;
    float4 hom2;
};
[vk::binding(0, 0)] ConstantBuffer<RpUIMatUniforms> rp_uimat_uniforms;
[vk::constant_id(0)] const bool RP_UIMAT_ANTIALIAS = true;

// UI-17c: perspective-correct centered-local from screenPos (see ui-rect.slang).
float2 rp_uimat_local(float2 fallbackLocal, float2 screenPos) {
    if (rp_uimat_uniforms.hom0.w < 0.5) return fallbackLocal;
    float3 l = rp_uimat_uniforms.hom0.xyz * screenPos.x + rp_uimat_uniforms.hom1.xyz * screenPos.y + rp_uimat_uniforms.hom2.xyz;
    return l.xy / l.z;
}

[shader("vertex")]
RpUIMatVSOutput vs_main(RpUIMatVSInput input) {
    RpUIMatVSOutput output;
    output.position = mul(float4(input.position, 0.0, 1.0), rp_uimat_uniforms.viewProjection);
    output.local = input.local;
    output.color = input.color;
    output.params = input.params;
    output.screenPos = input.position;
    return output;
}

// Rounded overflow-clip corners (see ui-rect.slang) — a material node inside a rounded scroll
// container clips to the container's corners, same as a plain fill.
float rp_uimat_clip(float2 screenPos) {
    if (rp_uimat_uniforms.clipParams.y < 0.5) return 1.0;
    float cd = sdRoundRect(screenPos - rp_uimat_uniforms.clipRect.xy,
                           rp_uimat_uniforms.clipRect.zw, rp_uimat_uniforms.clipParams.x);
    float caa = max(fwidth(cd), 1e-4);
    return 1.0 - smoothstep(-0.5 * caa, 0.5 * caa, cd);
}

[shader("fragment")]
float4 fs_main(RpUIMatVSOutput input) : SV_Target {
    float2 halfSize = max(input.params.xy, float2(1e-4));
    float radius = min(input.params.z, min(halfSize.x, halfSize.y));
    float2 rp_local = rp_uimat_local(input.local, input.screenPos);
    UIMaterialInput rp_mi;
    rp_mi.local = rp_local;
    rp_mi.uv = rp_local / (halfSize * 2.0) + 0.5;
    rp_mi.size = halfSize * 2.0;
    rp_mi.color = input.color;
    rp_mi.sdf = sdRoundRect(rp_local, halfSize, radius);
    rp_mi.radius = radius;
    rp_mi.border = input.params.w;
    rp_mi.time = rp_uimat_uniforms.aux.x;
    float rp_aa = RP_UIMAT_ANTIALIAS ? max(fwidth(rp_mi.sdf), 1e-4) : 1e-4;
    float rp_coverage = 1.0 - smoothstep(-0.5 * rp_aa, 0.5 * rp_aa, rp_mi.sdf);
    rp_coverage *= rp_uimat_clip(input.screenPos);
    ${name} rp_mat = {};
    float4 rp_shaded = rp_mat.shade(rp_mi);
    return float4(rp_shaded.rgb, rp_shaded.a * rp_coverage);
}
`;
  const userLineCount = source.split('\n').length;
  return { wrappedSource: source + '\n' + wrapper, userLineCount };
}

/**
 * v1 guard: the compiled module may bind NOTHING beyond the wrapper's uniforms (@group(0)
 * @binding(0)) — custom textures/samplers/cbuffers are materials v2 (reflection-driven params).
 * Returns an error message, or null when clean.
 */
export function checkUIMaterialWGSL(wgsl: string): string | null {
  const bindings = [...wgsl.matchAll(/@group\((\d+)\)\s*@binding\((\d+)\)/g)].map(
    (m) => `${m[1]}:${m[2]}`,
  );
  const extra = bindings.filter((b) => b !== '0:0');
  if (extra.length === 0) return null;
  return `custom material resources are not supported yet (v1) — found ${extra.length} extra binding(s); use UIMaterialInput only`;
}
